import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BoxSeam, FileEarmarkText } from 'react-bootstrap-icons';
import axios from 'axios';
import '../css/prodView.css';
import ProdContent from './ProdContent';
import fallbackImage from '../assets/fallback.jpg';
import SeoHead from './Shared/SeoHead';

const ProdView = () => {
    const siteUrl = window.location.origin;
    const { productName } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { imgDirectory, docsDirectory, prod, cat, products, selCat, searchTerm, currentPage, viewMode } = location.state || {};

    const [isLoading, setIsLoading] = useState(true);
    const [minLoadDone, setMinLoadDone] = useState(false);
    const [selectedThumbnail, setSelectedThumbnail] = useState(0);
    const [gallery, setGallery] = useState([]);
    const [desc, setDesc] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [about, setAbout] = useState([]);
    const [curProd, setCurProd] = useState(prod || null);
    const [imageDirectory, setImageDirectory] = useState(imgDirectory || '');
    const [docsPath, setDocsPath] = useState(docsDirectory || '');
    const bearerToken = localStorage.getItem('bearerToken');

    // Enforce a minimum 2-second branded loading screen.
    useEffect(() => {
        const timer = setTimeout(() => setMinLoadDone(true), 2000);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            setIsLoading(true);

            try {
                let product = curProd;

                if (!product) {
                    product = await getProductByName();
                }

                const directoriesPromise = (!imageDirectory || !docsPath) ? getDirectories() : Promise.resolve(null);

                if (!product || !product.proD_ID) {
                    navigate('/notfound', { replace: true });
                    return;
                }

                await Promise.all([
                    directoriesPromise,
                    getGalleryFiles(product.proD_ID),
                    getProductFeatures(product.proD_ID),
                    getProductDocumentation(product.proD_ID)
                ]);
            } catch (error) {
                console.error('Error in fetchData:', error);
                navigate('/notfound', { replace: true });
            }

            if (isMounted) {
                setIsLoading(false);
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getProductByName = async () => {
        try {
            const response = await axios.post('/Website/GetProductByName', {
                ProdName: productName
            }, {
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.data) {
                setCurProd(response.data);
                return response.data;
            }
        } catch (error) {
            console.error('Error fetching product by name:', error);
        }

        return null;
    };

    const getDirectories = async () => {
        try {
            const response = await axios.get('/Website/GetDirectories', {
                headers: { Authorization: `Bearer ${bearerToken}` }
            });

            if (response.data) {
                setImageDirectory(response.data.imgDirectory || '');
                setDocsPath(response.data.docsDirectory || '');
            }

            return response.data;
        } catch (error) {
            console.error('Error fetching image directory:', error);
            return null;
        }
    };

    const getGalleryFiles = async (prodID) => {
        if (!prodID) {
            return;
        }

        try {
            const response = await axios.post(
                '/Website/GetGalleryImagesByPath',
                prodID,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${bearerToken}`
                    }
                }
            );

            setGallery(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching gallery files:', error);
        }
    };

    const getProductFeatures = async (prodID) => {
        if (!prodID) return;

        try {
            const response = await axios.post(
                '/Website/GetDescriptionByProdID',
                prodID,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${bearerToken}`
                    }
                }
            );

            const arr = JSON.parse(response.data.desC_JSON || '{}');
            setDesc(arr?.list || []);
            setAbout(arr?.about || {});
            return arr;
        } catch (error) {
            console.error('Error fetching product features:', error);
            return null;
        }
    };

    const getProductDocumentation = async (prodID) => {
        if (!prodID) return;

        try {
            const response = await axios.post(
                '/Website/GetDocumentsbyProdID',
                prodID,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${bearerToken}`
                    }
                }
            );

            const docs = Array.isArray(response.data) ? response.data : [];
            setDocuments(docs);
            return docs;
        } catch (error) {
            console.error('Error fetching product documentation:', error);
            return [];
        }
    };

    const galleryItems = useMemo(() => {
        if (Array.isArray(gallery) && gallery.length > 0) {
            return gallery;
        }

        if (curProd?.imG_PATH) {
            return [{ imG_PATH: curProd.imG_PATH, imG_ID: curProd.proD_ID || curProd.proD_NAME }];
        }

        return [];
    }, [gallery, curProd]);

    const activeImage = galleryItems[selectedThumbnail] || galleryItems[0] || null;

    useEffect(() => {
        if (selectedThumbnail > galleryItems.length - 1) {
            setSelectedThumbnail(0);
        }
    }, [galleryItems, selectedThumbnail]);

    const handleThumbnailClick = (index) => {
        setSelectedThumbnail(index);
    };

    const getImageSrc = (imagePath) => {
        if (!imagePath || !imageDirectory) {
            return fallbackImage;
        }

        return `${imageDirectory}/${imagePath}`;
    };

    const productTitle = curProd?.proD_NAME || productName || 'Product details';
    const productDescription = curProd?.proD_DESC || 'No description available for this product yet.';
    //const aboutEntries = about && typeof about === 'object' && !Array.isArray(about) ? Object.entries(about) : [];
    const canonicalProductUrl = `${siteUrl}/ProdView/${encodeURIComponent(productTitle)}`;
    const productSchema = {
        '@context': 'https://schema.org',
        '@type': 'Product',

        name: productTitle,
        description: productDescription,
        url: canonicalProductUrl,
        mainEntityOfPage: canonicalProductUrl,

        image: activeImage?.imG_PATH && imageDirectory
            ? [`${imageDirectory}/${activeImage.imG_PATH}`]
            : [],

        sku: productTitle,
        mpn: productTitle,

        brand: {
            '@type': 'Brand',
            name: 'MHE Electronics'
        },

        manufacturer: {
            '@type': 'Organization',
            name: 'MHE Electronics',
            url: siteUrl,
            logo: `${siteUrl}/MHELogo.svg`
        },

        offers: {
            '@type': 'Offer',
            url: canonicalProductUrl,
            priceCurrency: 'ZAR',
            availability: 'https://schema.org/InStock',
            seller: {
                '@type': 'Organization',
                name: 'MHE Electronics'
            }
        },

        category: 'Manufacturer original product',

        additionalProperty: Array.isArray(desc)
            ? desc.map((item, index) => ({
                '@type': 'PropertyValue',
                name: `Feature ${index + 1}`,
                value: item
            }))
            : []
    };

    const backToProductsState = {
        cat: Array.isArray(cat) ? cat : [],
        products: Array.isArray(products) ? products : [],
        selCat: selCat || 0,
        searchTerm: searchTerm || '',
        currentPage: currentPage || 1,
        viewMode: viewMode || 'grid',
        restoreFilters: true
    };

    if (isLoading || !minLoadDone) {
        return (
            <div className="prodview-loading-screen">
                <div className="prodview-loader-ring">
                    <svg className="prodview-loader-svg" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="40" cy="40" r="34" stroke="#e5e7eb" strokeWidth="8" />
                        <circle cx="40" cy="40" r="34" stroke="#ac130a" strokeWidth="8"
                            strokeLinecap="round"
                            strokeDasharray="100 114"
                            strokeDashoffset="25"
                            className="prodview-loader-arc" />
                    </svg>
                </div>
                <p className="prodview-loading-label">Loading product details…</p>
            </div>
        );
    }

    return (
        <div className="product-page">
            <SeoHead
                title={`MHE Electronics | ${productTitle} | Official Product Page`}
                description={`${productTitle} by MHE Electronics. Official manufacturer product page with specifications, features, images, manuals, and supporting documentation.`}
                canonical={canonicalProductUrl}
                schema={productSchema}
                ogType="product"
            />
            <div className="prodview-shell">
                <div className="prodview-breadcrumb-row">
                    <Link to="/Products" state={backToProductsState} className="prodview-back-link">
                        <ArrowLeft size={18} />
                        <span>Back to Products</span>
                    </Link>
                </div>

                <section className="prodview-hero">
                    <div className="prodview-gallery-panel">
                        <div className="prodview-gallery-frame">
                            {activeImage ? (
                                <img key={`${activeImage.imG_PATH || 'fallback'}-${selectedThumbnail}`}
                                    src={getImageSrc(activeImage.imG_PATH)}
                                    alt={productTitle}
                                    className="prodview-main-image"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = fallbackImage;
                                    }}
                                />
                            ) : (
                                <img src={fallbackImage} alt={productTitle} className="prodview-main-image"/>
                            )}
                        </div>

                        {galleryItems.length > 1 && (
                            <div className="prodview-thumbnail-strip">
                                {galleryItems.map((image, index) => (
                                    <button key={`${image.imG_ID || productTitle}-${index}`}
                                        type="button"
                                        className={`prodview-thumb-btn ${selectedThumbnail === index ? 'active' : ''}`}
                                        onClick={() => handleThumbnailClick(index)}
                                        aria-label={`View image ${index + 1}`}
                                    >
                                        <img src={getImageSrc(image.imG_PATH)}
                                            alt={`${productTitle} thumbnail ${index + 1}`}
                                            className="prodview-thumb-image"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = fallbackImage;
                                            }}
                                        />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="prodview-summary-panel">
                        <span className="prodview-eyebrow">Product overview</span>
                        <h1 className="prodview-title">{productTitle}</h1>
                        <p className="prodview-description">{productDescription}</p>

                        <div className="prodview-stat-grid">
                            <div className="prodview-stat-card">
                                <div className="prodview-stat-icon"><BoxSeam size={18} /></div>
                                <div>
                                    <span className="prodview-stat-label">Highlights</span>
                                    <strong className="prodview-stat-value">
                                        {desc.length} {desc.length === 1 ? 'feature' : 'features'}
                                    </strong>
                                </div>
                            </div>
                            <div className="prodview-stat-card">
                                <div className="prodview-stat-icon"><FileEarmarkText size={18} /></div>
                                <div>
                                    <span className="prodview-stat-label">Documents</span>
                                    <strong className="prodview-stat-value">
                                        {documents.length} {documents.length === 1 ? 'file' : 'files'}
                                    </strong>
                                </div>
                            </div>
                        </div>

                        {desc.length > 0 && (
                            <div className="prodview-feature-card">
                                <ul className="prodview-feature-list">
                                    {desc.slice(0, 4).map((item, index) => (
                                        <li key={`${item}-${index}`}>
                                            <ArrowRight size={14} className="prodview-feature-arrow" />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="prodview-authority-card">
                            <span className="prodview-authority-label">About the product</span>
                            <p className="prodview-authority-copy">
                                <strong>Manufactured by MHE Electronics.</strong> {productTitle} is designed,
                                developed and manufactured by MHE Electronics.
                            </p>
                        </div>
                    </div>
                </section>

                <ProdContent description={desc} documents={documents} docsDir={docsPath} about={about}/>
            </div>
        </div>
    );
};

export default ProdView;